import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Factory, PackageCheck, Boxes, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import ShopWoSideDrawer from './ShopWoSideDrawer';

const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-200',
  Released: 'bg-blue-100 text-blue-800 border-blue-200',
  InProgress: 'bg-amber-100 text-amber-800 border-amber-200',
  OnHold: 'bg-rose-100 text-rose-800 border-rose-200',
  Completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Closed: 'bg-slate-900 text-white border-slate-900',
  Canceled: 'bg-red-100 text-red-800 border-red-200',
};

/**
 * LinkedShopWosPanel — live status of each spawned shop-floor WO under a WOv2.
 * Shows: WO#, status pill, progress %, units completed, breakdown, lots used.
 */
const LinkedShopWosPanel = ({ api, woId, onProgress }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [drawer, setDrawer] = useState(null);

  const load = useCallback(async (showSpinner = true) => {
    if (!woId || woId === 'new') { setLoading(false); return; }
    if (showSpinner) setLoading(true); else setRefreshing(true);
    try {
      const r = await api().get(`/work-orders-v2/${woId}/shop-wos`);
      setData(r.data);
      // iter106 — push aggregate progress to the WOv2 header
      if (onProgress) {
        const shops = r.data?.shop_wos || [];
        const target = shops.reduce((a, s) => a + Number(s.target_quantity || 0), 0);
        const done   = shops.reduce((a, s) => a + Number(s.completed_quantity || 0), 0);
        onProgress({ count: shops.length, target, completed: done, pct: target ? Math.round((done / target) * 100) : 0 });
      }
    } catch { /* non-blocking */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [api, woId, onProgress]);
  useEffect(() => { load(); }, [load]);

  if (!woId || woId === 'new') return null;

  const shops = (data && data.shop_wos) || [];

  return (
    <Card className="industrial-card" data-testid="linked-shop-wos-panel">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Factory className="w-4 h-4 text-slate-600" />
          Linked Shop-Floor Work Orders
          <span className="text-xs text-slate-400 font-normal">{shops.length}</span>
        </CardTitle>
        <Button size="sm" variant="ghost" className="h-7 rounded-sm text-xs" onClick={() => load(false)} disabled={refreshing} data-testid="linked-shop-wos-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : shops.length === 0 ? (
          <div className="text-sm text-slate-500 italic">
            No shop-floor Work Orders have been spawned yet. Release this WO to automatically create one shop WO per BOM-backed line.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {shops.map((s) => (
              <div key={s.shop_work_order_id} className="border border-slate-200 rounded-sm p-3 bg-white hover:border-sky-400 hover:shadow-sm transition cursor-pointer" onClick={() => !s.missing && setDrawer(s)} data-testid={`shop-wo-tile-${s.shop_work_order_number}`}>
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="flex items-center gap-2">
                    <span className="mono font-bold text-sm">{s.shop_work_order_number}</span>
                    <Badge className={`${STATUS_COLOR[s.status] || 'bg-slate-100 text-slate-600 border-slate-200'} border rounded-sm text-[11px]`}>{s.status || '—'}</Badge>
                    {s.approval_status && s.approval_status !== 'approved' && (
                      <Badge variant="outline" className="rounded-sm text-[11px] border-amber-300 text-amber-700">{s.approval_status}</Badge>
                    )}
                    {s.missing && <Badge variant="outline" className="rounded-sm text-[11px] text-red-600 border-red-200">missing</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mono">{s.completed_quantity}/{s.target_quantity} units</div>
                </div>

                {!s.missing && (
                  <>
                    <div className="mt-3">
                      <Progress value={Math.min(100, s.progress_pct || 0)} className="h-2" />
                      <div className="text-[11px] text-slate-500 mt-1 flex justify-between">
                        <span>{s.progress_pct}% complete</span>
                        <span className="mono">{s.units_count} unit record{s.units_count === 1 ? '' : 's'}</span>
                      </div>
                    </div>

                    {Object.keys(s.unit_status_breakdown || {}).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1" data-testid={`shop-wo-breakdown-${s.shop_work_order_number}`}>
                        {Object.entries(s.unit_status_breakdown).map(([status, count]) => (
                          <Badge key={status} variant="outline" className="rounded-sm text-[10px]">
                            <PackageCheck className="w-3 h-3 mr-1" /> {status} · {count}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {(s.lots_consumed || []).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`shop-wo-lots-${s.shop_work_order_number}`}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1">
                          <Boxes className="w-3 h-3" /> Lots Consumed
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {s.lots_consumed.slice(0, 12).map((l) => (
                            <Badge key={l.lot} variant="outline" className="rounded-sm text-[10px] mono" title={l.component_skus.join(', ')}>
                              {l.lot} <span className="text-slate-500 ml-1">({l.qty})</span>
                            </Badge>
                          ))}
                          {s.lots_consumed.length > 12 && (
                            <Badge variant="outline" className="rounded-sm text-[10px] text-slate-400">+{s.lots_consumed.length - 12} more</Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {s.notes && <div className="mt-3 text-[11px] text-slate-500 italic line-clamp-2">{s.notes}</div>}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <ShopWoSideDrawer
        open={!!drawer}
        onOpenChange={(v) => { if (!v) setDrawer(null); }}
        api={api}
        shopWoSummary={drawer}
        onChanged={() => load(false)}
      />
    </Card>
  );
};

export default LinkedShopWosPanel;
